import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { MenuItem } from 'primeng/api';
import { GenerateCvService } from 'src/app/pages/generate resume/generate-cv-steps/services/generate-cv/generate-cv.service';
import { CvDbService } from 'src/app/services/cv-db/cv-db.service';
import { ImageService } from 'src/app/services/image/image.service';
import { PaymentService } from 'src/app/services/payment/payment.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-create-cover-letter',
  templateUrl: './create-cover-letter.component.html',
  styleUrls: ['./create-cover-letter.component.css'],
})
export class CreateCoverLetterComponent implements OnInit {

  @ViewChild('stepsSection') stepsSection!: ElementRef;

  services = [
    {
      color: 'blue',
      img: '../../../assets/icon/headline.svg',
      title: 'Headline',
      description: [
        'File format and size',
        'Resume length',
        'Long bullet points with suggestions on how to shorten',
      ],
    },
    {
      color: 'pink',
      img: '../../../assets/icon/summary.svg',
      title: 'Summary',
      description: [
        'Contact information',
        'Essential sections',
        'Personality showcase with tips on how to improve',
      ],
    },
    {
      color: 'blue',
      img: '../../../assets/icon/contact.svg',
      title: 'Contacts',
      description: [
        'ATS parse rate',
        'Repetition of words and phrases',
        'Spelling and grammar',
        'Quantifying impact in experience section with examples',
      ],
    },
    {
      color: 'pink',
      img: '../../../assets/icon/experience.png',
      title: 'Experience',
      description: ['Hard skills', 'Soft skills'],
    },
    {
      color: 'blue',
      img: '../../../assets/icon/educatiion.svg',
      title: 'Education',
      description: [
        'Resume design',
        'Email address',
        'Usage of active voice',
        'Usage of buzzwords and cliches',
      ],
    },
  ];

  steps: MenuItem[] = [];
  activeIndex: number = 0;
  clFile: any;
  loading: boolean = false;
  userId!: any;
  imageUrl?: SafeUrl;
  serviceSystemId: string = '';
  isWordPurchased: boolean = false;
  loadingPurchaseStatus: boolean = true;
  userInfo?: any = JSON.parse(localStorage.getItem('userInfo') || '{}');
  clUrlImage: string = '';

  jobDetailsForm!: FormGroup;

  constructor(
    private translate: TranslateService,
    private fb: FormBuilder,
    private cvService: GenerateCvService,
    private paymentService: PaymentService,
    private router: Router,
    private route: ActivatedRoute,
    private gtmService: GoogleTagManagerService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private cvDbService: CvDbService,
    private imageService: ImageService
  ) {
    this.userId = localStorage.getItem('userId');
    this.getTranslate();
    this.translate.onLangChange.subscribe(() => {
      this.getTranslate();
    });
  }

  ngOnInit() {
    this.initializeJobDetailsForm();

    this.route.queryParams.subscribe((params) => {
      const queryServiceId = params['serviceSystemId'];

      if (queryServiceId) {
        this.serviceSystemId = queryServiceId;
        this.activeIndex = 2;
        this.loadCoverLetterDataFromApi();
      } else {
        this.serviceSystemId = localStorage.getItem('clServiceSystemId') || '';
      }
    });

    this.cvService.data$.subscribe((data) => {
      if (!data) return;

      const imageFile = data?.clImagePath || null;
      const serviceId = data['serviceSystemId'];

      if (serviceId) {
        this.serviceSystemId = serviceId;
        localStorage.setItem('clServiceSystemId', serviceId);
      }

      if (imageFile) {
        this.loadCoverLetterImage(imageFile);
      }

      this.cvDbService.savePdf(data);
    });

    const pendingFile = sessionStorage.getItem('pendingCoverLetterFile');
    const pendingFileName = sessionStorage.getItem('pendingCoverLetterFileName');
    const pendingFileType = sessionStorage.getItem('pendingCoverLetterFileType');

    if (pendingFile && pendingFileName && pendingFileType && this.userId) {
      sessionStorage.removeItem('pendingCoverLetterFile');
      sessionStorage.removeItem('pendingCoverLetterFileName');
      sessionStorage.removeItem('pendingCoverLetterFileType');

      const byteString = atob(pendingFile);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: pendingFileType });
      this.clFile = new File([blob], pendingFileName, { type: pendingFileType });

      setTimeout(() => this.scrollToSteps(), 300);
      this.processFile();
    }
  }

  initializeJobDetailsForm() {
    this.jobDetailsForm = this.fb.group({
      jobTitle: [''],
      company: [''],
      jobDescription: [''],
    });
  }

  getTranslate() {
    this.translate
      .get(['Upload CV', 'Job Details', 'Review'])
      .subscribe((translations) => {
        this.steps = [
          { label: translations['Upload CV'] },
          { label: translations['Job Details'] },
          { label: translations['Review'] },
        ];
      });
  }

  scrollToSteps() {
    if (this.stepsSection && this.stepsSection.nativeElement) {
      this.stepsSection.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private saveFileToPending(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        sessionStorage.setItem('pendingCoverLetterFile', base64);
        sessionStorage.setItem('pendingCoverLetterFileName', file.name);
        sessionStorage.setItem('pendingCoverLetterFileType', file.type);
        resolve();
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  processFile() {
    this.activeIndex = 1;
  }

  onBack() {
    this.activeIndex = 0;
  }

  onGenerateCoverLetter() {
    this.activeIndex = 2;
    this.loading = true;

    const data = new FormData();
    data.append('cv', this.clFile);

    const jobDetails = this.jobDetailsForm.value;

    if (jobDetails.jobTitle && jobDetails.jobTitle.trim()) {
      data.append('jobTitle', jobDetails.jobTitle.trim());
    }

    if (jobDetails.company && jobDetails.company.trim()) {
      data.append('company', jobDetails.company.trim());
    }

    if (jobDetails.jobDescription && jobDetails.jobDescription.trim()) {
      data.append('jobDescription', jobDetails.jobDescription.trim());
    }

    this.cvService.createCoverLetter(this.userId, data).subscribe({
      next: (res: any) => {
        if (res.id) {
          this.serviceSystemId = res.id;
          localStorage.setItem('clServiceSystemId', res.id);
        }

        if (res.imagePath) {
          localStorage.setItem('clImagePath', res.imagePath);
          this.loadCoverLetterImage(res.imagePath);
        }
      },
      error: (err) => {
        this.activeIndex = 0;
        console.error('Error creating cover letter:', err);
        this.loading = false;
      },
    });
  }

  onLinkedinDrop(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      this.clFile = event.dataTransfer.files[0];
      if (!this.userId) {
        this.saveFileToPending(this.clFile).then(() => {
          localStorage.setItem('redirectUrl', '/create-cover-letter');
          this.router.navigate(['/login']);
        });
        return;
      }
      this.processFile();
    }
  }

  onLinkedinSelected(event: any) {
    this.clFile = event.target.files[0];
    if (this.clFile) {
      if (!this.userId) {
        this.saveFileToPending(this.clFile).then(() => {
          localStorage.setItem('redirectUrl', '/create-cover-letter');
          this.router.navigate(['/login']);
        });
        return;
      }
      this.processFile();
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  loadCoverLetterDataFromApi() {
    if (!this.serviceSystemId) return;

    this.loadingPurchaseStatus = true;

    this.paymentService
      .checkIsGeneratedCLPurchased(this.serviceSystemId)
      .subscribe({
        next: (res: any) => {
          this.isWordPurchased = res.isPaid || false;

          const imagePath = res.imagePath || null;

          if (imagePath) {
            this.loadCoverLetterImage(imagePath);
            if (res.generatedImageCLpath) {
              localStorage.setItem('clImagePath', res.generatedImageCLpath);
            }
          }

          this.loadingPurchaseStatus = false;
        },
        error: (err) => {
          console.error('Error loading cover letter data:', err);
          this.loadingPurchaseStatus = false;

          const cachedImagePath = localStorage.getItem('clImagePath');
          if (cachedImagePath) {
            this.loadCoverLetterImage(cachedImagePath);
          }
        },
      });
  }

  loadCoverLetterImage(imagePath: string) {
    if (imagePath) {
      this.clUrlImage = 'assets/img/placeholder.png';
      const fullImageUrl = `${
        environment.apiImagePath
      }assets/cls/${imagePath}?t=${new Date().getTime()}`;

      this.imageService.getSecureImage(fullImageUrl).subscribe({
        next: (blobUrl) => {
          this.clUrlImage = blobUrl;
          this.loading = false;
        },
        error: (err) => {
          console.error('Error loading image:', err);
          this.clUrlImage = 'assets/img/error-placeholder.png';
          this.loading = false;
        },
      });
    }
  }

  goToPayment(productKey: string) {
    this.router.navigate(['/payment'], {
      state: {
        productKey: productKey,
        serviceSystemId: this.serviceSystemId,
      },
      queryParams: {
        productKey: productKey,
        serviceSystemId: this.serviceSystemId,
      },
    });
  }

  downloadFile(base64Data: string, filename: string) {
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      const binaryData = this.base64ToArrayBuffer(base64Data);
      const blob = new Blob([binaryData], { type: 'application/pdf' });
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      this.sendGoogleEvent('download cover letter');
    }
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    let binaryString = '';
    if (typeof window !== 'undefined') {
      binaryString = window.atob(base64);
    }
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  sendGoogleEvent(name: string) {
    this.googleAnalyticsService.event(name);
    this.gtmService.pushTag({
      event: 'customEvent',
      category: 'button',
      action: 'click',
      label: name,
    });
  }

  onDownloadPdf() {
    if (!this.isWordPurchased) {
      this.goToPayment('generate-cl');
      return;
    }

    this.loadingPurchaseStatus = true;

    this.cvService.downloadCoverLetter(this.serviceSystemId).subscribe({
      next: (response: any) => {
        if (response && response.coverLetterData) {
          this.downloadFile(response.coverLetterData, 'CoverLetter.pdf');
        }
        this.loadingPurchaseStatus = false;
      },
      error: (err) => {
        console.error('Error downloading cover letter:', err);
        this.loadingPurchaseStatus = false;
      },
    });
  }

  createNewCoverLetter() {
    this.activeIndex = 0;
    this.serviceSystemId = '';
    this.clUrlImage = '';
    this.isWordPurchased = false;
    this.imageUrl = undefined;
    this.jobDetailsForm.reset();
    localStorage.removeItem('clServiceSystemId');
    localStorage.removeItem('clImagePath');
  }
}